import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import Seo from "../../components/feature/Seo";
import ComicCard from "../../components/feature/ComicCard";
import { comics, categories, hotTags } from "../../mocks/comics";

const statusOptions = ["全部", "连载中", "已完结"];
const regionOptions = ["全部", "国产", "日漫", "美漫"];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") || "");

  const cat = searchParams.get("cat") || "全部";
  const status = searchParams.get("status") || "全部";
  const tag = searchParams.get("tag") || "";
  const region = searchParams.get("region") || "全部";

  const filtered = useMemo(() => {
    let list = comics;
    if (cat !== "全部") list = list.filter((c) => c.category === cat);
    if (status !== "全部") list = list.filter((c) => c.status === status);
    if (region !== "全部") list = list.filter((c) => c.region === region);
    if (tag) list = list.filter((c) => c.tags.includes(tag));
    const q = searchParams.get("q") || "";
    if (q) {
      list = list.filter(
        (c) =>
          c.title.includes(q) ||
          c.author.includes(q) ||
          c.tags.some((t) => t.includes(q))
      );
    }
    return list;
  }, [cat, status, region, tag, searchParams]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "全部" || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateParam("q", keyword.trim());
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <Seo
        title="策驰漫画网漫画库 - 全部分类书库与筛选"
        description="策驰漫画网漫画库分类齐全，支持按分类、状态、地区和标签筛选，人气排行、编辑精选与完结作品一网打尽，快速定位你想看的漫画。"
        canonical="https://artbinge.github.io/fenlei"
      />

      {/* 页头 */}
      <section className="pt-16 md:pt-[72px] bg-primary-600 relative overflow-hidden">
        <div className="absolute -top-12 -right-10 w-52 h-52 rounded-full bg-accent-500/30 blur-2xl"></div>
        <div className="relative z-10 mx-auto max-w-7xl w-full px-4 md:px-6 py-10 md:py-12">
          <nav aria-label="面包屑" className="text-primary-100 text-xs md:text-sm mb-3">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-background-50">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-background-50">漫画库</li>
            </ol>
          </nav>
          <h1 className="font-heading text-3xl md:text-4xl text-background-50">漫画分类书库</h1>
          <p className="mt-2 text-primary-100 text-sm md:text-base">分类齐全、人气排行、编辑精选，快速定位你想看的漫画</p>
        </div>
      </section>

      {/* 筛选区 */}
      <section className="mx-auto max-w-7xl w-full px-4 md:px-6 -mt-1 py-8 md:py-10">
        {/* 搜索 */}
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row items-stretch gap-3 mb-6 max-w-xl">
          <div className="flex flex-1 items-center gap-2 bg-background-100 border border-background-200 rounded-full px-4">
            <i className="ri-search-line text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索标题 / 作者 / 标签..."
              aria-label="搜索漫画"
              className="flex-1 h-11 bg-transparent text-sm text-foreground-900 outline-none"
            />
          </div>
          <button type="submit" className="whitespace-nowrap h-11 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors">
            搜索
          </button>
        </form>

        {/* 分类 */}
        <div className="mb-5">
          <h3 className="text-xs font-bold text-foreground-600 mb-2.5 flex items-center gap-1.5"><i className="ri-grid-fill text-accent-600"></i>漫画分类</h3>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => updateParam("cat", "全部")} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${cat === "全部" ? "bg-primary-500 text-background-50" : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-secondary-100"}`}>全部</button>
            {categories.map((c) => (
              <button key={c.name} onClick={() => updateParam("cat", c.name)} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${cat === c.name ? "bg-primary-500 text-background-50" : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-secondary-100"}`}>{c.name}</button>
            ))}
          </div>
        </div>

        {/* 状态 & 地区 */}
        <div className="flex flex-wrap gap-x-8 gap-y-4 mb-5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-foreground-600">状态</span>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((s) => (
                <button key={s} onClick={() => updateParam("status", s)} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${status === s ? "bg-secondary-500 text-background-50" : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-secondary-100"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-foreground-600">地区</span>
            <div className="flex flex-wrap gap-2">
              {regionOptions.map((r) => (
                <button key={r} onClick={() => updateParam("region", r)} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${region === r ? "bg-secondary-500 text-background-50" : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-secondary-100"}`}>{r}</button>
              ))}
            </div>
          </div>
        </div>

        {/* 标签 */}
        <div className="mb-6">
          <h3 className="text-xs font-bold text-foreground-600 mb-2.5 flex items-center gap-1.5"><i className="ri-price-tag-3-fill text-accent-600"></i>热门标签</h3>
          <div className="flex flex-wrap gap-2">
            {hotTags.map((t) => (
              <button key={t} onClick={() => updateParam("tag", tag === t ? "" : t)} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${tag === t ? "bg-accent-500 text-foreground-950" : "bg-background-100 border border-background-200 text-foreground-600 hover:bg-accent-100"}`}>#{t}</button>
            ))}
          </div>
        </div>

        {/* 结果 */}
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-foreground-600">共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 部作品</p>
          {(cat !== "全部" || status !== "全部" || region !== "全部" || tag || searchParams.get("q")) && (
            <button onClick={() => setSearchParams({}, { replace: true })} className="text-xs text-foreground-500 hover:text-primary-600 inline-flex items-center gap-1">
              <i className="ri-refresh-line"></i> 清除筛选
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {filtered.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center bg-background-100 rounded-2xl border border-background-200/70">
            <i className="ri-inbox-2-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-600">没有找到符合条件的漫画，换个筛选条件试试吧</p>
            <button onClick={() => setSearchParams({}, { replace: true })} className="mt-5 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600">查看全部</button>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}